import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { Alert, Button, ButtonGroup, CircularProgress, Paper, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Field, ProcessModule } from 'src/API';
import infiniteCanvasApi from 'src/api/infiniteCanvas/InfiniteCanvasApi';
import { FieldInput } from 'src/api/process-module';
import { isDebugMode } from 'src/config';
import logger from 'src/log/log';
import { paths } from 'src/paths';
import { useSelector } from 'src/store';

interface ProcessModuleInfiniteCanvasPreviewProps {
  processModule?: ProcessModule;
  teamId: string;
  formFieldConfig?: FieldInput & Partial<Field>;
  onActionLink: (data: any) => void;
  value: any;
  onActionUnlink: (data: any) => void;
  isPreview?: boolean;
}

export const ProcessModuleInfiniteCanvasPreview = (props: ProcessModuleInfiniteCanvasPreviewProps) => {
  const { teamId, formFieldConfig, value, isPreview } = props;
  const [isCreatingCanvas, setIsCreatingCanvas] = useState(false);
  const currentTeam = useSelector((state) => state.team.current);

  // Check if canvas ID is available
  const canvasId = formFieldConfig?.properties?.canvasId;
  const templateId = formFieldConfig?.properties?.templateId;

  // Removed debug logging

  if (!canvasId) {
    return (
      <Stack spacing={1} direction="column">
        <Paper style={{ padding: 10 }}>
          <Alert severity="error">
            Canvas not configured. Please contact your administrator to set up this canvas field.
          </Alert>
        </Paper>
      </Stack>
    );
  }

  // Handle canvas creation and opening
  const handleClick = async () => {
    if (isPreview || isCreatingCanvas) return;

    setIsCreatingCanvas(true);

    try {
      // Get canvas name
      const canvasName = formFieldConfig?.label || formFieldConfig?.name || 'Canvas';

      logger.log('FormView - Creating canvas with:', {
        teamId: currentTeam?.id || teamId,
        canvasId,
        templateId,
        name: canvasName,
        hasTemplateId: !!templateId,
        templateIdValue: templateId || 'EMPTY',
      });

      // Make request to backend to get the actual docId
      const response = await infiniteCanvasApi.getOrCreateInfiniteCanvas({
        teamId: currentTeam?.id || teamId,
        canvasId: canvasId,
        config: {
          templateId: templateId,
          name: canvasName,
        },
      });

      if (response.ok && response.result?.docId) {
        // Construct the canvas path with the docId from the response
        const canvasPath = paths.infiniteCanvas.canvas(teamId, response.result.docId, {
          templateId: templateId,
          name: canvasName,
        });

        logger.log('FormView - Canvas path generated:', {
          canvasPath,
          templateId,
          hasTemplateId: !!templateId,
        });

        window.open(canvasPath, '_blank');
        toast.success('Canvas opened in new tab');
      } else {
        logger.error('FormView - Canvas creation failed:', response);
        toast.error('Failed to create canvas');
      }
    } catch (error) {
      logger.error('FormView - Error creating canvas:', error);
      toast.error('Failed to create canvas');
    } finally {
      setIsCreatingCanvas(false);
    }
  };

  // Check if the field is enabled
  const isEnabled = !formFieldConfig?.properties?.disabled;

  // if (!processModule) {
  //   return null;
  // }
  return (
    <Stack spacing={1} direction="column">
      <Stack direction={'column'} spacing={2}>
        <Paper
          style={{
            padding: 10,
          }}
        >
          <Stack direction={'column'} spacing={2}>
            {formFieldConfig?.label ? (
              <Typography variant="body2" color="text.secondary">
                {formFieldConfig?.label}
              </Typography>
            ) : null}
            {/* {isDebugMode() && <pre>{JSON.stringify({ props }, null, 2)}</pre>} */}
            {/* {isDebugMode() && <pre>canvasPath: {canvasPath}</pre>} */}
            <ButtonGroup
              style={{
                boxShadow: 'none',
              }}
              variant="contained"
              color="primary"
            >
              <Button
                startIcon={isCreatingCanvas ? <CircularProgress size={16} /> : <AllInclusiveIcon />}
                onClick={handleClick}
                title={(value || 'Open Canvas') + ' ' + canvasId}
                disabled={!isEnabled || isCreatingCanvas}
              >
                {isCreatingCanvas
                  ? 'Opening Canvas...'
                  : formFieldConfig?.properties?.override?.label || 'Open Canvas' + isDebugMode()
                    ? ` [${templateId || 'no template'}]`
                    : ''}
              </Button>
              {/* First button for opening the canvas */}
            </ButtonGroup>
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
};
